/**
 * @file kpiCompare.ts
 * @brief This month so far vs the same days of last month, for the company
 *        dashboard's KPI deltas (DES 03-10-2026).
 *
 * @details
 * A month-to-date figure is compared with the same slice of last month
 * (1st → the same day, end of day), not the whole of last month, or every
 * month would look like a drop until its last day. The phone's Usage cards
 * use the same rule. `percentChange` returns null when last month's slice
 * held nothing: there is no honest percentage from zero, so the card says
 * "New" instead of "+100%".
 *
 * @author DES
 * @date 03-10-2026
 */

/** Start of last month → the same day-of-month (clamped), end of that day. */
export function previousMonthToDate(now: Date = new Date()): { from: Date; to: Date } {
  const from = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
  const lastDay = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
  const day = Math.min(now.getDate(), lastDay);
  const to = new Date(now.getFullYear(), now.getMonth() - 1, day, 23, 59, 59, 999);
  return { from, to };
}

/** Whole-number % change; null when there is nothing to compare against. */
export function percentChange(current: number, previous: number): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  if (previous <= 0) return current > 0 ? null : 0;
  return Math.round(((current - previous) / previous) * 100);
}
